import type { IAddItemToBasketRequest } from '../../redux/slices/basket/basketService'
import type { ISimpleBasket } from '../../redux/slices/basket/basketApi'
import { ChatAddBlock, ChatProduct } from './types'

/** Karttan tek seferde eklenebilecek en çok adet (spec §3.3 MaxQuantityPerLine). */
export const MAX_CHAT_BASKET_QUANTITY = 999

const FALLBACK_ERROR = 'Ürün sepete eklenemedi. Lütfen daha sonra tekrar deneyin.'

export type ChatAddStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'added'; quantity: number }
  | { kind: 'error'; message: string }

export type ChatAddToBasketResult =
  | { ok: true; basket: ISimpleBasket; quantity: number }
  | { ok: false; message: string }

export interface ChatAddBlockNote {
  text: string
  link?: { label: string; to: string }
}

/** Adet 1..999; stok biliniyorsa (> 0) üst sınır stok. Son kararı yine portal verir. */
export function clampChatBasketQuantity(value: number, stock?: number | null): number {
  const upper = stock && stock > 0 ? Math.min(stock, MAX_CHAT_BASKET_QUANTITY) : MAX_CHAT_BASKET_QUANTITY
  if (!Number.isFinite(value)) return 1
  return Math.min(Math.max(Math.floor(value), 1), upper)
}

/** Portalın `basket2/addToBasketUI` gövdesi — sitenin AddToBasket bileşeniyle aynı alanlar. */
export function buildChatAddToBasketRequest(product: ChatProduct, quantity: number): IAddItemToBasketRequest {
  const opportunityId = product.opportunityId ?? null
  return {
    ProductId: product.productId as number,
    Quantity: clampChatBasketQuantity(quantity, product.stock),
    IsOppurtunityStock: opportunityId !== null,
    OpportunitiyStockId: opportunityId,
  }
}

/**
 * Portal çağrısını yapar ve sonucu kart durumuna çevirir. `ClientProxy` iş hatasını
 * `{ description }` olarak fırlatır (ör. "En fazla N adet…"); mesaj olduğu gibi gösterilir.
 */
export async function submitChatAddToBasket(
  product: ChatProduct,
  quantity: number,
  add: (request: IAddItemToBasketRequest) => Promise<ISimpleBasket>
): Promise<ChatAddToBasketResult> {
  if (!product.addable || !product.productId) return { ok: false, message: FALLBACK_ERROR }

  const request = buildChatAddToBasketRequest(product, quantity)
  try {
    const basket = await add(request)
    return { ok: true, basket, quantity: request.Quantity }
  } catch (error) {
    const description = (error as { description?: unknown } | null)?.description
    return {
      ok: false,
      message: typeof description === 'string' && description.trim() ? description : FALLBACK_ERROR,
    }
  }
}

/** Buton yerine gösterilecek not; not gerektirmeyen kodlarda (stok/fiyat/katalog) null. */
export function chatAddBlockNote(block: ChatAddBlock | undefined): ChatAddBlockNote | null {
  switch (block) {
    case 'iquote':
      return { text: 'Bu ürün HPE iQuote ile yapılandırılır; ürün sayfasındaki iQuote bağlantısını kullanın.' }
    case 'kaspersky':
      return {
        text: 'Kaspersky lisansları Kaspersky sayfasından eklenir.',
        link: { label: 'Kaspersky sayfası', to: '/kaspersky' },
      }
    default:
      return null
  }
}
